<script setup lang="ts">
import { computed } from 'vue'
import type { HTMLAttributes } from "vue"
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/shadcn'
import { cn } from "@/lib/utils"

type CardVariant = 'default' | 'elevated'

const props = withDefaults(
  defineProps<{
    /** Card 자체의 클래스 */
    class?: HTMLAttributes["class"]
    /** 카드 제목 */
    title?: string
    /** 카드 설명 */
    description?: string
    /** 카드 푸터 텍스트 */
    footer?: string
    /** 카드 variant (패턴 4) */
    variant?: CardVariant
    /** CardContent가 남은 세로 공간을 채우도록 flex column 적용 */
    fillContent?: boolean
  }>(),
  {
    variant: 'default',
  }
)

// 패턴 4: variant 클래스
const variantClasses = computed(() => {
  const variants = {
    default: 'shadow-sm',
    elevated: 'shadow-md',
  }
  return variants[props.variant]
})
</script>

<template>
  <Card :class="cn(variantClasses, fillContent && 'flex flex-col', props.class)">
    <!-- Header: title/description + actions 슬롯 (패턴 7) -->
    <CardHeader v-if="title || description || $slots.actions">
      <div class="flex justify-between items-center gap-3">
        <!-- 좌측: 제목/설명 -->
        <div class="flex-1">
          <CardTitle v-if="title">{{ title }}</CardTitle>
          <CardDescription v-if="description">{{ description }}</CardDescription>
        </div>

        <!-- 우측: actions 슬롯 (패턴 7) -->
        <div v-if="$slots.actions" class="flex gap-2 flex-shrink-0">
          <slot name="actions" />
        </div>
      </div>
    </CardHeader>

    <!-- Content -->
    <CardContent :class="fillContent && 'flex-1 flex flex-col min-h-0'">
      <slot />
    </CardContent>

    <!-- Footer: 슬롯 우선, 없으면 텍스트 -->
    <CardFooter v-if="$slots.footer || footer">
      <slot name="footer">{{ footer }}</slot>
    </CardFooter>
  </Card>
</template>
